import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  InputGroup,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { hasAnyPermission, hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import '../../styles/admin.css';
import './AdminEventosDetallePage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const formatEventDate = (diaseventos) => {
  if (!diaseventos || !Array.isArray(diaseventos) || diaseventos.length === 0) {
    return 'Sin fecha especificada';
  }
  const firstDate = new Date(diaseventos[0]);
  if (isNaN(firstDate.getTime())) {
    return 'Fecha inválida';
  }
  return firstDate.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const AdminEventosDetalleInner = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();

  // Verificar permisos
  const hasEditEvents = useMemo(() => hasPermission(user, PERMISSION_IDS.EDITAR_EVENTOS), [user]);
  const hasActivateEvents = useMemo(() => hasPermission(user, PERMISSION_IDS.ACTIVAR_EVENTOS), [user]);
  const hasArchiveEvents = useMemo(() => hasPermission(user, PERMISSION_IDS.ARCHIVAR_EVENTOS), [user]);
  const canViewEventDetail = useMemo(
    () =>
      hasAnyPermission(user, [
        PERMISSION_IDS.VER_EVENTOS,
        PERMISSION_IDS.GESTIONAR_EVENTOS,
        PERMISSION_IDS.PANEL_EVENTOS,
        PERMISSION_IDS.CREAR_EVENTOS,
        PERMISSION_IDS.EDITAR_EVENTOS,
      ]),
    [user],
  );

  useEffect(() => {
    if (user && !canViewEventDetail) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para ver este evento.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, canViewEventDetail, navigate, notify]);

  const [event, setEvent] = useState(null);
  const [eventClasses, setEventClasses] = useState([]);
  const [eventDays, setEventDays] = useState([]);
  const [eventCodes, setEventCodes] = useState([]);
  const [codeStats, setCodeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingDays, setLoadingDays] = useState(false);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [pending, setPending] = useState(false);

  // Estados de edición
  const [editState, setEditState] = useState({
    title: { value: '', editing: false },
    shortDescription: { value: '', editing: false },
    fullDescription: { value: '', editing: false },
    link: { value: '', editing: false },
    direccion: { value: '', editing: false },
    cupos: { value: 0, editing: false },
  });

  // Estados de modales
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [showClassModal, setShowClassModal] = useState(false);
  const [classDraft, setClassDraft] = useState({
    id: null,
    tituloclaseevento: '',
    descripcionbreve: '',
    descripcioncompleta: '',
    diaclase: '',
    orderIndex: 0,
  });
  const [classLoading, setClassLoading] = useState(false);
  const [showDayModal, setShowDayModal] = useState(false);
  const [dayDraft, setDayDraft] = useState({
    id: null,
    fecha: '',
    titulo: '',
    descripcion: '',
    horaInicio: '',
    horaFin: '',
  });
  const [dayLoading, setDayLoading] = useState(false);
  const [showGenerateCodeModal, setShowGenerateCodeModal] = useState(false);
  const [codeQuantity, setCodeQuantity] = useState(10);
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeFilter, setCodeFilter] = useState('all'); // all, available, assigned, used, cancelled
  const [currentPage, setCurrentPage] = useState(1);
  const codesPerPage = 5;
  
  // Estados para controlar contenedores colapsables
  const [isDaysExpanded, setIsDaysExpanded] = useState(false);
  const [isClassesExpanded, setIsClassesExpanded] = useState(false);
  const [isCodesExpanded, setIsCodesExpanded] = useState(false);

  const fetchEvent = useCallback(async () => {
    if (!token || !eventId) return;
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl(`/api/events/${eventId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`No se pudo cargar el evento (${response.status})`);
      }

      const data = await response.json();
      const eventData = data.event || data;
      setEvent(eventData);
      setEditState({
        title: { value: eventData.tituloevento || eventData.nombre || '', editing: false },
        shortDescription: { value: eventData.descripcionbreve || eventData.descripcionBreve || '', editing: false },
        fullDescription: { value: eventData.descripcioncompleta || eventData.descripcionExtendida || '', editing: false },
        link: { value: eventData.formato === 'presencial' ? (eventData.linkpresencial || eventData.linkPresencial || '') : (eventData.linkvirtual || eventData.linkVirtual || ''), editing: false },
        direccion: { value: eventData.direccion || '', editing: false },
        cupos: { value: eventData.cuposdisponibles || eventData.cupos || 0, editing: false },
      });
    } catch (error) {
      notify({
        title: 'Error al cargar el evento',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [eventId, notify, token]);

  const fetchEventClasses = useCallback(async () => {
    if (!token || !eventId) return;
    setLoadingClasses(true);
    try {
      const response = await fetch(buildApiUrl(`/api/events/${eventId}/classes`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('No se pudieron cargar las clases del evento');
      }

      const data = await response.json();
      setEventClasses(Array.isArray(data) ? data : []);
    } catch (error) {
      logError('Error cargando clases:', error);
    } finally {
      setLoadingClasses(false);
    }
  }, [eventId, token]);

  const fetchEventDays = useCallback(async () => {
    if (!token || !eventId) return;
    setLoadingDays(true);
    try {
      const response = await fetch(buildApiUrl(`/api/events/${eventId}/days`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('No se pudieron cargar los días del evento');
      }

      const data = await response.json();
      setEventDays(Array.isArray(data) ? data : []);
    } catch (error) {
      logError('Error cargando días:', error);
    } finally {
      setLoadingDays(false);
    }
  }, [eventId, token]);

  const fetchEventCodes = useCallback(async () => {
    if (!token || !eventId) return;
    setLoadingCodes(true);
    try {
      const response = await fetch(buildApiUrl(`/api/event-codes/${eventId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('No se pudieron cargar los códigos del evento');
      }

      const data = await response.json();
      if (data.success && data.data) {
        setEventCodes(Array.isArray(data.data.codes) ? data.data.codes : []);
      }
    } catch (error) {
      logError('Error cargando códigos:', error);
    } finally {
      setLoadingCodes(false);
    }
  }, [eventId, token]);

  const fetchCodeStats = useCallback(async () => {
    if (!token || !eventId) return;
    try {
      const response = await fetch(buildApiUrl(`/api/event-codes/${eventId}/stats`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('No se pudieron cargar las estadísticas de códigos');
      }

      const data = await response.json();
      if (data.success && data.data) {
        setCodeStats(data.data.stats);
      }
    } catch (error) {
      logError('Error cargando estadísticas de códigos:', error);
    }
  }, [eventId, token]);

  useEffect(() => {
    if (token && eventId) {
      fetchEvent();
      fetchEventClasses();
      fetchEventDays();
      fetchEventCodes();
      fetchCodeStats();
    }
  }, [token, eventId, fetchEvent, fetchEventClasses, fetchEventDays, fetchEventCodes, fetchCodeStats]);

  const refreshAll = async () => {
    setPending(true);
    await Promise.all([fetchEvent(), fetchEventClasses(), fetchEventDays(), fetchEventCodes(), fetchCodeStats()]);
    setPending(false);
    notify({
      title: 'Datos actualizados',
      message: 'Se sincronizaron los datos del evento exitosamente.',
      variant: 'success',
    });
  };

  const updateEvent = useCallback(async (updateData) => {
    if (!token || !eventId) return;
    setPending(true);
    try {
      const response = await fetch(buildApiUrl(`/api/events/${eventId}`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar el evento');
      }

      const data = await response.json();
      const updatedEvent = data.event || data;
      setEvent(updatedEvent);
      return updatedEvent;
    } catch (error) {
      notify({
        title: 'Error',
        message: error.message,
        variant: 'error',
      });
      throw error;
    } finally {
      setPending(false);
    }
  }, [eventId, token, notify]);

  const handleSaveTitle = useCallback(async () => {
    try {
      await updateEvent({ tituloevento: editState.title.value });
      setEditState((prev) => ({ ...prev, title: { ...prev.title, editing: false } }));
      notify({ title: 'Éxito', message: 'Título actualizado correctamente', variant: 'success' });
    } catch (error) {
      // Error ya manejado en updateEvent
    }
  }, [editState.title.value, updateEvent, notify]);

  const handleSaveShortDescription = useCallback(async () => {
    try {
      await updateEvent({ descripcionbreve: editState.shortDescription.value });
      setEditState((prev) => ({ ...prev, shortDescription: { ...prev.shortDescription, editing: false } }));
      notify({ title: 'Éxito', message: 'Descripción breve actualizada correctamente', variant: 'success' });
    } catch (error) {
      // Error ya manejado en updateEvent
    }
  }, [editState.shortDescription.value, updateEvent, notify]);

  const handleSaveFullDescription = useCallback(async () => {
    try {
      await updateEvent({ descripcioncompleta: editState.fullDescription.value });
      setEditState((prev) => ({ ...prev, fullDescription: { ...prev.fullDescription, editing: false } }));
      notify({ title: 'Éxito', message: 'Descripción completa actualizada correctamente', variant: 'success' });
    } catch (error) {
      // Error ya manejado en updateEvent
    }
  }, [editState.fullDescription.value, updateEvent, notify]);

  const handleSaveLink = useCallback(async () => {
    try {
      const updateData = event.formato === 'presencial'
        ? { linkpresencial: editState.link.value || null }
        : { linkvirtual: editState.link.value || null };
      await updateEvent(updateData);
      setEditState((prev) => ({ ...prev, link: { ...prev.link, editing: false } }));
      notify({ title: 'Éxito', message: 'Link actualizado correctamente', variant: 'success' });
    } catch (error) {
      // Error ya manejado en updateEvent
    }
  }, [editState.link.value, event?.formato, updateEvent, notify]);

  const handleSaveDireccion = useCallback(async () => {
    try {
      await updateEvent({ direccion: editState.direccion.value || null });
      setEditState((prev) => ({ ...prev, direccion: { ...prev.direccion, editing: false } }));
      notify({ title: 'Éxito', message: 'Dirección actualizada correctamente', variant: 'success' });
    } catch (error) {
      // Error ya manejado en updateEvent
    }
  }, [editState.direccion.value, updateEvent, notify]);

  const handleSaveCupos = useCallback(async () => {
    try {
      await updateEvent({ cuposdisponibles: parseInt(editState.cupos.value) || 0 });
      setEditState((prev) => ({ ...prev, cupos: { ...prev.cupos, editing: false } }));
      notify({ title: 'Éxito', message: 'Cupos actualizados correctamente', variant: 'success' });
    } catch (error) {
      // Error ya manejado en updateEvent
    }
  }, [editState.cupos.value, updateEvent, notify]);

  const handleImageUpload = useCallback(async () => {
    if (!imageFile) {
      notify({ title: 'Error', message: 'Por favor selecciona una imagen', variant: 'error' });
      return;
    }

    setImageLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await fetch(buildApiUrl(`/api/events/${eventId}/image`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Error al subir la imagen');
      }

      const data = await response.json();
      setEvent((prev) => ({ ...prev, imagenevento: data.imagePath || data.event?.imagenevento }));
      setShowImageModal(false);
      setImageFile(null);
      notify({ title: 'Éxito', message: 'Imagen actualizada correctamente', variant: 'success' });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    } finally {
      setImageLoading(false);
    }
  }, [imageFile, eventId, token, notify]);

  const handleToggleActivate = useCallback(async () => {
    if (!window.confirm(`¿Estás seguro de que quieres ${event.activo ? 'desactivar' : 'activar'} este evento?`)) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/events/${eventId}/activate`), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ activo: !event.activo }),
      });

      if (!response.ok) {
        throw new Error('Error al cambiar el estado del evento');
      }

      const data = await response.json();
      setEvent((prev) => ({ ...prev, activo: data.event?.activo ?? !prev.activo }));
      notify({
        title: 'Éxito',
        message: `Evento ${data.event?.activo ? 'activado' : 'desactivado'} correctamente`,
        variant: 'success',
      });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    }
  }, [event?.activo, eventId, token, notify]);

  const handleToggleArchive = useCallback(async () => {
    if (!window.confirm(`¿Estás seguro de que quieres ${event.archivo ? 'desarchivar' : 'archivar'} este evento?`)) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/events/${eventId}/archive`), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ archivo: !event.archivo }),
      });

      if (!response.ok) {
        throw new Error('Error al cambiar el estado de archivo');
      }

      const data = await response.json();
      setEvent((prev) => ({ ...prev, archivo: data.event?.archivo ?? !prev.archivo }));
      notify({
        title: 'Éxito',
        message: `Evento ${data.event?.archivo ? 'archivado' : 'desarchivado'} correctamente`,
        variant: 'success',
      });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    }
  }, [event?.archivo, eventId, token, notify]);

  const handleChangeFormat = useCallback(async () => {
    const newFormat = event.formato === 'presencial' ? 'virtual' : 'presencial';
    if (!window.confirm(`¿Estás seguro de que quieres cambiar el formato a ${newFormat}?`)) {
      return;
    }

    try {
      await updateEvent({ formato: newFormat });
      notify({ title: 'Éxito', message: 'Formato actualizado correctamente', variant: 'success' });
    } catch (error) {
      // Error ya manejado en updateEvent
    }
  }, [event?.formato, updateEvent, notify]);

  const handleSaveClass = useCallback(async () => {
    if (!classDraft.tituloclaseevento.trim()) {
      notify({ title: 'Error', message: 'El título de la clase es requerido', variant: 'error' });
      return;
    }

    setClassLoading(true);
    try {
      const url = classDraft.id
        ? buildApiUrl(`/api/events/${eventId}/classes/${classDraft.id}`)
        : buildApiUrl(`/api/events/${eventId}/classes`);

      const method = classDraft.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tituloclaseevento: classDraft.tituloclaseevento.trim(),
          descripcionbreve: classDraft.descripcionbreve || null,
          descripcioncompleta: classDraft.descripcioncompleta || null,
          diaclase: classDraft.diaclase || null,
          // orderIndex se asigna automáticamente en el backend, no es necesario enviarlo
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar la clase');
      }

      setShowClassModal(false);
      setClassDraft({ id: null, tituloclaseevento: '', descripcionbreve: '', descripcioncompleta: '', diaclase: '', orderIndex: 0 });
      await fetchEventClasses();
      notify({
        title: 'Éxito',
        message: `Clase ${classDraft.id ? 'actualizada' : 'creada'} correctamente`,
        variant: 'success',
      });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    } finally {
      setClassLoading(false);
    }
  }, [classDraft, eventId, token, notify, fetchEventClasses]);

  const handleDeleteClass = useCallback(async (classId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta clase?')) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/events/${eventId}/classes/${classId}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al eliminar la clase');
      }

      await fetchEventClasses();
      notify({ title: 'Éxito', message: 'Clase eliminada correctamente', variant: 'success' });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    }
  }, [eventId, token, notify, fetchEventClasses]);

  const handleSaveDay = useCallback(async () => {
    if (!dayDraft.fecha || !dayDraft.titulo.trim()) {
      notify({ title: 'Error', message: 'La fecha y el título son requeridos', variant: 'error' });
      return;
    }

    setDayLoading(true);
    try {
      const url = dayDraft.id
        ? buildApiUrl(`/api/events/${eventId}/days/${dayDraft.id}`)
        : buildApiUrl(`/api/events/${eventId}/days`);

      const method = dayDraft.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fecha: dayDraft.fecha,
          titulo: dayDraft.titulo.trim(),
          descripcion: dayDraft.descripcion || null,
          horaInicio: dayDraft.horaInicio || null,
          horaFin: dayDraft.horaFin || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar el día');
      }

      setShowDayModal(false);
      setDayDraft({ id: null, fecha: '', titulo: '', descripcion: '', horaInicio: '', horaFin: '' });
      await fetchEventDays();
      notify({
        title: 'Éxito',
        message: `Día ${dayDraft.id ? 'actualizado' : 'creado'} correctamente`,
        variant: 'success',
      });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    } finally {
      setDayLoading(false);
    }
  }, [dayDraft, eventId, token, notify, fetchEventDays]);

  const handleDeleteDay = useCallback(async (dayId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este día?')) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/events/${eventId}/days/${dayId}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al eliminar el día');
      }

      await fetchEventDays();
      notify({ title: 'Éxito', message: 'Día eliminado correctamente', variant: 'success' });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    }
  }, [eventId, token, notify, fetchEventDays]);

  const handleGenerateCodes = useCallback(async () => {
    if (!codeQuantity || codeQuantity <= 0 || codeQuantity > 1000) {
      notify({ title: 'Error', message: 'La cantidad debe estar entre 1 y 1000', variant: 'error' });
      return;
    }

    setCodeLoading(true);
    try {
      const response = await fetch(buildApiUrl(`/api/event-codes/generate/${eventId}`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quantity: parseInt(codeQuantity) }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al generar códigos');
      }

      const data = await response.json();
      setShowGenerateCodeModal(false);
      setCodeQuantity(10);
      await Promise.all([fetchEventCodes(), fetchCodeStats()]);
      notify({
        title: 'Éxito',
        message: data.message || `${codeQuantity} códigos generados correctamente`,
        variant: 'success',
      });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    } finally {
      setCodeLoading(false);
    }
  }, [codeQuantity, eventId, token, notify, fetchEventCodes, fetchCodeStats]);

  const handleCancelCode = useCallback(async (codeId) => {
    if (!window.confirm('¿Estás seguro de que quieres cancelar este código?')) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/event-codes/${codeId}/cancel`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al cancelar el código');
      }

      await Promise.all([fetchEventCodes(), fetchCodeStats()]);
      notify({ title: 'Éxito', message: 'Código cancelado correctamente', variant: 'success' });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    }
  }, [token, notify, fetchEventCodes, fetchCodeStats]);

  // Calcular códigos filtrados y paginados
  const filteredCodes = useMemo(() => {
    return eventCodes.filter((code) => codeFilter === 'all' || code.status === codeFilter);
  }, [eventCodes, codeFilter]);

  const totalPages = Math.ceil(filteredCodes.length / codesPerPage);
  const startIndex = (currentPage - 1) * codesPerPage;
  const endIndex = startIndex + codesPerPage;
  const paginatedCodes = filteredCodes.slice(startIndex, endIndex);

  // Resetear a página 1 cuando cambia el filtro
  useEffect(() => {
    setCurrentPage(1);
  }, [codeFilter]);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-layout">
          <AdminSidebar activeKey="events" />
          <main className="admin-main">
            <div className="AEDP-container">
              <div className="AEDP-loading">
                <Spinner animation="border" />
                <p>Cargando evento...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="admin-dashboard">
        <div className="admin-layout">
          <AdminSidebar activeKey="events" />
          <main className="admin-main">
            <div className="AEDP-container">
              <div className="AEDP-error">
                <i className="bi-exclamation-triangle" />
                <h3>Evento no encontrado</h3>
                <Button variant="primary" onClick={() => navigate('/admin/events')}>
                  Volver a eventos
                </Button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar
          activeKey="events"
          onNavigate={(item) => {
            if (item.path) {
              navigate(item.path);
            }
          }}
        />
        <main className="admin-main">
          <div className="AEDP-container">
            {/* Header */}
            <div className="AEDP-header">
              <div className="AEDP-header-content">
                <Button variant="link" className="AEDP-back-button" onClick={() => navigate(-1)}>
                  <i className="bi-arrow-left-circle me-2" />
                  Volver
                </Button>
                <h1 className="AEDP-title">
                  <i className="bi-calendar-event" />
                  {loading ? 'Cargando evento...' : (event?.tituloevento || event?.nombre || 'Evento sin título')}
                </h1>
                <p className="AEDP-subtitle">
                  Gestiona la información y clases del evento seleccionado.
                </p>
              </div>
              <div className="AEDP-actions">
                <Button variant="outline-secondary" onClick={refreshAll} disabled={pending}>
                  {pending ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Actualizando
                    </>
                  ) : (
                    <>
                      <i className="bi-arrow-repeat me-2" />
                      Sincronizar datos
                    </>
                  )}
                </Button>
                {hasArchiveEvents && (
                  <Button
                    variant={event.archivo ? 'success' : 'outline-secondary'}
                    onClick={handleToggleArchive}
                    disabled={pending}
                  >
                    <i className={`bi-${event.archivo ? 'arrow-counterclockwise' : 'archive'} me-2`} />
                    {event.archivo ? 'Desarchivar' : 'Archivar'}
                  </Button>
                )}
                {hasActivateEvents && !event.activo && (
                  <Button variant="primary" onClick={handleToggleActivate} disabled={pending}>
                    <i className="bi-play-circle me-2" />
                    Activar evento
                  </Button>
                )}
              </div>
            </div>

            {/* Event Card */}
            <div className="AEDP-event-card">
              <div className="AEDP-event-image-wrapper">
                {event.imagenevento ? (
                  <div className="AEDP-image-section">
                    <img
                      src={resolveImageUrl(event.imagenevento)}
                      alt={event.tituloevento || event.nombre}
                      className="AEDP-event-image"
                    />
                    {hasEditEvents && (
                      <div className="AEDP-image-overlay">
                        <button
                          type="button"
                          className="AEDP-change-image-btn"
                          onClick={() => setShowImageModal(true)}
                        >
                          <i className="bi-camera-video" />
                          Cambiar imagen
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="AEDP-image-placeholder">
                    <i className="bi-calendar-event" />
                    <p>Sin imagen</p>
                    {hasEditEvents && (
                      <div className="AEDP-image-overlay">
                        <button
                          type="button"
                          className="AEDP-change-image-btn"
                          onClick={() => setShowImageModal(true)}
                        >
                          <i className="bi-camera-video" />
                          Cambiar imagen
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="AEDP-event-content">

                {/* Badges */}
                <div className="AEDP-badges-container">
                  <Badge bg={event.activo ? 'success' : 'secondary'}>
                    {event.activo ? 'Activo' : 'Inactivo'}
                  </Badge>
                  {event.archivo && <Badge bg="dark">Archivado</Badge>}
                  <Badge bg={event.formato === 'presencial' ? 'primary' : 'info'}>
                    {event.formato === 'presencial' ? 'Presencial' : 'Virtual'}
                  </Badge>
                </div>

                {/* Title Section */}
                <div className="AEDP-title-section">
                  <div className="AEDP-title-edit">
                    {editState.title.editing ? (
                      <>
                        <Form.Control
                          value={editState.title.value}
                          onChange={(e) =>
                            setEditState((prev) => ({
                              ...prev,
                              title: { ...prev.title, value: e.target.value },
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveTitle();
                            if (e.key === 'Escape') {
                              setEditState((prev) => ({
                                ...prev,
                                title: { ...prev.title, editing: false, value: event.tituloevento || event.nombre || '' },
                              }));
                            }
                          }}
                          autoFocus
                          className="AEDP-title-input"
                          maxLength={120}
                        />
                        <div className="AEDP-title-actions">
                          <Button variant="success" onClick={handleSaveTitle} disabled={pending}>
                            Guardar
                          </Button>
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              setEditState((prev) => ({
                                ...prev,
                                title: { ...prev.title, editing: false, value: event.tituloevento || event.nombre || '' },
                              }));
                            }}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <h2 className="AEDP-event-title-text">{event.tituloevento || event.nombre || 'Evento sin título'}</h2>
                        {hasEditEvents && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              setEditState((prev) => ({
                                ...prev,
                                title: { ...prev.title, editing: true },
                              }))
                            }
                          >
                            <i className="bi-pencil" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                  <p className="AEDP-event-id">
                    ID del evento: <code>{event.id}</code>
                  </p>
                </div>

                {/* Description Section */}
                <div className="AEDP-description-section">
                  <div className="AEDP-description-item">
                    <div className="AEDP-description-header">
                      <h3 className="AEDP-description-title">Descripción breve</h3>
                      <div className="AEDP-description-actions">
                        {editState.shortDescription.editing ? (
                          <>
                            <Button variant="success" size="sm" onClick={handleSaveShortDescription} disabled={pending}>
                              Guardar
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                setEditState((prev) => ({
                                  ...prev,
                                  shortDescription: {
                                    ...prev.shortDescription,
                                    editing: false,
                                    value: event.descripcionbreve || event.descripcionBreve || '',
                                  },
                                }));
                              }}
                            >
                              Cancelar
                            </Button>
                          </>
                        ) : (
                          hasEditEvents && (
                            <button
                              className="AEDP-description-edit-btn"
                              onClick={() =>
                                setEditState((prev) => ({
                                  ...prev,
                                  shortDescription: { ...prev.shortDescription, editing: true },
                                }))
                              }
                              title="Editar descripción breve"
                            >
                              <i className="bi-pencil" />
                            </button>
                          )
                        )}
                      </div>
                    </div>
                    {editState.shortDescription.editing ? (
                      <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength={320}
                        value={editState.shortDescription.value}
                        onChange={(e) =>
                          setEditState((prev) => ({
                            ...prev,
                            shortDescription: { ...prev.shortDescription, value: e.target.value },
                          }))
                        }
                        placeholder="Añade una descripción breve para destacar el evento."
                      />
                    ) : (
                      <p className="AEDP-description-content" data-placeholder="Añade una descripción breve para destacar el evento.">
                        {event.descripcionbreve || event.descripcionBreve || 'Añade una descripción breve para destacar el evento.'}
                      </p>
                    )}
                  </div>

                  <div className="AEDP-description-item">
                    <div className="AEDP-description-header">
                      <h3 className="AEDP-description-title">Descripción completa</h3>
                      <div className="AEDP-description-actions">
                        {editState.fullDescription.editing ? (
                          <>
                            <Button variant="success" size="sm" onClick={handleSaveFullDescription} disabled={pending}>
                              Guardar
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                setEditState((prev) => ({
                                  ...prev,
                                  fullDescription: {
                                    ...prev.fullDescription,
                                    editing: false,
                                    value: event.descripcioncompleta || event.descripcionExtendida || '',
                                  },
                                }));
                              }}
                            >
                              Cancelar
                            </Button>
                          </>
                        ) : (
                          hasEditEvents && (
                            <button
                              className="AEDP-description-edit-btn"
                              onClick={() =>
                                setEditState((prev) => ({
                                  ...prev,
                                  fullDescription: { ...prev.fullDescription, editing: true },
                                }))
                              }
                              title="Editar descripción completa"
                            >
                              <i className="bi-pencil" />
                            </button>
                          )
                        )}
                      </div>
                    </div>
                    {editState.fullDescription.editing ? (
                      <Form.Control
                        as="textarea"
                        rows={6}
                        maxLength={4000}
                        value={editState.fullDescription.value}
                        onChange={(e) =>
                          setEditState((prev) => ({
                            ...prev,
                            fullDescription: { ...prev.fullDescription, value: e.target.value },
                          }))
                        }
                        placeholder="Agrega una descripción completa para informar a los participantes."
                      />
                    ) : (
                      <p className="AEDP-description-content" data-placeholder="Agrega una descripción completa para informar a los participantes.">
                        {event.descripcioncompleta || event.descripcionExtendida || 'Agrega una descripción completa para informar a los participantes.'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Additional Info Fields */}
                <div className="AEDP-info-section">
                  {/* Formato */}
                  <div className="AEDP-info-card">
                    <div className="AEDP-info-card-header">
                      <label className="AEDP-info-label">
                        <i className="bi-calendar3 me-2" />
                        Formato
                      </label>
                    </div>
                    <div className="AEDP-info-card-body">
                      <div className="AEDP-format-display">
                        <div className={`AEDP-format-indicator ${event.formato === 'presencial' ? 'AEDP-format-presencial' : 'AEDP-format-virtual'}`}>
                          <i className={`bi-${event.formato === 'presencial' ? 'geo-alt' : 'camera-video'} me-2`} />
                          {event.formato === 'presencial' ? 'Presencial' : 'Virtual'}
                        </div>
                        {hasEditEvents && (
                          <button
                            type="button"
                            className="AEDP-format-toggle-btn"
                            onClick={handleChangeFormat}
                            disabled={pending}
                          >
                            <i className="bi-arrow-repeat" />
                            Cambiar a {event.formato === 'presencial' ? 'Virtual' : 'Presencial'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Link */}
                  <div className="AEDP-info-card">
                    <div className="AEDP-info-card-header">
                      <label className="AEDP-info-label">
                        <i className={`bi-${event.formato === 'presencial' ? 'geo-alt' : 'link-45deg'} me-2`} />
                        Link {event.formato === 'presencial' ? 'Presencial' : 'Virtual'}
                      </label>
                    </div>
                    <div className="AEDP-info-card-body">
                      {editState.link.editing ? (
                        <div className="AEDP-link-edit">
                          <Form.Control
                            value={editState.link.value}
                            onChange={(e) =>
                              setEditState((prev) => ({
                                ...prev,
                                link: { ...prev.link, value: e.target.value },
                              }))
                            }
                            placeholder={`Ingresa el link ${event.formato === 'presencial' ? 'presencial' : 'virtual'}`}
                            className="AEDP-link-input"
                          />
                          <div className="AEDP-link-actions">
                            <button
                              type="button"
                              className="AEDP-link-save-btn"
                              onClick={handleSaveLink}
                              disabled={pending}
                            >
                              <i className="bi-check" />
                              Guardar
                            </button>
                            <button
                              type="button"
                              className="AEDP-link-cancel-btn"
                              onClick={() => {
                                const currentLink = event.formato === 'presencial'
                                  ? (event.linkpresencial || event.linkPresencial || '')
                                  : (event.linkvirtual || event.linkVirtual || '');
                                setEditState((prev) => ({
                                  ...prev,
                                  link: { ...prev.link, editing: false, value: currentLink },
                                }));
                              }}
                            >
                              <i className="bi-x" />
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="AEDP-link-display">
                          <span className={`AEDP-link-value ${(event.formato === 'presencial' ? (event.linkpresencial || event.linkPresencial) : (event.linkvirtual || event.linkVirtual)) ? '' : 'AEDP-link-empty'}`}>
                            {event.formato === 'presencial'
                              ? (event.linkpresencial || event.linkPresencial || 'No especificado')
                              : (event.linkvirtual || event.linkVirtual || 'No especificado')}
                          </span>
                          {hasEditEvents && (
                            <button
                              type="button"
                              className="AEDP-link-edit-btn"
                              onClick={() =>
                                setEditState((prev) => ({
                                  ...prev,
                                  link: { ...prev.link, editing: true },
                                }))
                              }
                            >
                              <i className="bi-pencil" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dirección (solo para eventos presenciales) */}
                  {event.formato === 'presencial' && (
                    <div className="AEDP-info-card">
                      <div className="AEDP-info-card-header">
                        <label className="AEDP-info-label">
                          <i className="bi-geo-alt-fill me-2" />
                          Dirección de Referencia
                        </label>
                      </div>
                      <div className="AEDP-info-card-body">
                        {editState.direccion.editing ? (
                          <div className="AEDP-link-edit">
                            <Form.Control
                              value={editState.direccion.value}
                              onChange={(e) =>
                                setEditState((prev) => ({
                                  ...prev,
                                  direccion: { ...prev.direccion, value: e.target.value },
                                }))
                              }
                              placeholder="Ej: Av. Victorica 5557, Moreno, Buenos Aires"
                              className="AEDP-link-input"
                            />
                            <div className="AEDP-link-actions">
                              <button
                                type="button"
                                className="AEDP-link-save-btn"
                                onClick={handleSaveDireccion}
                                disabled={pending}
                              >
                                <i className="bi-check" />
                                Guardar
                              </button>
                              <button
                                type="button"
                                className="AEDP-link-cancel-btn"
                                onClick={() => {
                                  const currentDireccion = event.direccion || '';
                                  setEditState((prev) => ({
                                    ...prev,
                                    direccion: { ...prev.direccion, editing: false, value: currentDireccion },
                                  }));
                                }}
                              >
                                <i className="bi-x" />
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="AEDP-link-display">
                            <span className={`AEDP-link-value ${event.direccion ? '' : 'AEDP-link-empty'}`}>
                              {event.direccion || 'No especificado'}
                            </span>
                            {hasEditEvents && (
                              <button
                                type="button"
                                className="AEDP-link-edit-btn"
                                onClick={() =>
                                  setEditState((prev) => ({
                                    ...prev,
                                    direccion: { ...prev.direccion, editing: true },
                                  }))
                                }
                              >
                                <i className="bi-pencil" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cupos */}
                  <div className="AEDP-info-card">
                    <div className="AEDP-info-card-header">
                      <label className="AEDP-info-label">
                        <i className="bi-people me-2" />
                        Cupos Disponibles
                      </label>
                    </div>
                    <div className="AEDP-info-card-body">
                      {editState.cupos.editing ? (
                        <div className="AEDP-link-edit">
                          <Form.Control
                            type="number"
                            value={editState.cupos.value}
                            onChange={(e) =>
                              setEditState((prev) => ({
                                ...prev,
                                cupos: { ...prev.cupos, value: parseInt(e.target.value) || 0 },
                              }))
                            }
                            min="0"
                            className="AEDP-link-input"
                          />
                          <div className="AEDP-link-actions">
                            <button
                              type="button"
                              className="AEDP-link-save-btn"
                              onClick={handleSaveCupos}
                              disabled={pending}
                            >
                              <i className="bi-check" />
                              Guardar
                            </button>
                            <button
                              type="button"
                              className="AEDP-link-cancel-btn"
                              onClick={() => {
                                setEditState((prev) => ({
                                  ...prev,
                                  cupos: {
                                    ...prev.cupos,
                                    editing: false,
                                    value: event.cuposdisponibles || event.cupos || 0,
                                  },
                                }));
                              }}
                            >
                              <i className="bi-x" />
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="AEDP-link-display">
                          <span className="AEDP-link-value">
                            {event.cuposdisponibles || event.cupos || 0}
                          </span>
                          {hasEditEvents && (
                            <button
                              type="button"
                              className="AEDP-link-edit-btn"
                              onClick={() =>
                                setEditState((prev) => ({
                                  ...prev,
                                  cupos: { ...prev.cupos, editing: true },
                                }))
                              }
                            >
                              <i className="bi-pencil" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Días del evento */}
            <section className="AEDP-days-section">
              <div className="AEDP-days-container">
                <div className="AEDP-days-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    <button
                      type="button"
                      onClick={() => setIsDaysExpanded(!isDaysExpanded)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#495057'
                      }}
                    >
                      <i className={`bi-chevron-${isDaysExpanded ? 'down' : 'right'}`} style={{ fontSize: '1.25rem' }} />
                    </button>
                    <div>
                      <h2 className="AEDP-days-title">Días del Evento</h2>
                      <p className="AEDP-days-subtitle">
                        Organiza los días del evento y asigna clases a cada uno.
                      </p>
                    </div>
                  </div>
                  {hasEditEvents && (
                    <Button variant="primary" onClick={() => {
                      setDayDraft({ id: null, fecha: '', titulo: '', descripcion: '', horaInicio: '', horaFin: '' });
                      setShowDayModal(true);
                    }}>
                      <i className="bi-plus-circle me-2" />
                      Agregar Fecha
                    </Button>
                  )}
                </div>

                {isDaysExpanded && (
                <div className="AEDP-days-content">
                  {loadingDays ? (
                    <div className="AEDP-loading">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2">Cargando días...</span>
                    </div>
                  ) : eventDays.length === 0 ? (
                    <div className="AEDP-empty-state">
                      <i className="bi-calendar-x" />
                      <h5 className="AEDP-empty-title">Aún no hay días creados</h5>
                      <p>Organiza el evento creando días con sus fechas y descripciones.</p>
                      {hasEditEvents && (
                        <button className="AEDP-empty-classes-button" onClick={() => {
                          setDayDraft({ id: null, fecha: '', titulo: '', descripcion: '', horaInicio: '', horaFin: '' });
                          setShowDayModal(true);
                        }}>
                          <i className="bi-plus-circle" />
                          Crear primer día
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="AEDP-days-grid">
                      {eventDays.map((day) => (
                        <div key={day.id} className="AEDP-day-card">
                          <div className="AEDP-day-header">
                            <div>
                              <h5 className="AEDP-day-title">{day.titulo}</h5>
                              <div className="AEDP-day-date">
                                <i className="bi-calendar3" />
                                <span>
                                  {new Date(day.fecha).toLocaleDateString('es-ES', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </span>
                                {(day.horaInicio || day.horaFin) && (
                                  <span className="AEDP-day-time">
                                    <i className="bi-clock" />
                                    {day.horaInicio ? (
                                      <>
                                        {day.horaInicio.substring(0, 5)}
                                        {day.horaFin && ` - ${day.horaFin.substring(0, 5)}`}
                                      </>
                                    ) : (
                                      day.horaFin && day.horaFin.substring(0, 5)
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                            {hasEditEvents && (
                              <div className="AEDP-day-actions">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => {
                                    setDayDraft({
                                      id: day.id,
                                      fecha: day.fecha,
                                      titulo: day.titulo,
                                      descripcion: day.descripcion || '',
                                      horaInicio: day.horaInicio || '',
                                      horaFin: day.horaFin || '',
                                    });
                                    setShowDayModal(true);
                                  }}
                                >
                                  <i className="bi-pencil" />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteDay(day.id)}
                                >
                                  <i className="bi-trash" />
                                </Button>
                              </div>
                            )}
                          </div>
                          {day.descripcion && (
                            <div className="AEDP-day-body">
                              <p className="AEDP-day-description">{day.descripcion}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}
              </div>
            </section>

            {/* Clases del evento */}
            <section className="AEDP-classes-section">
              <div className="AEDP-classes-container">
                <div className="AEDP-classes-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    <button
                      type="button"
                      onClick={() => setIsClassesExpanded(!isClassesExpanded)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#495057'
                      }}
                    >
                      <i className={`bi-chevron-${isClassesExpanded ? 'down' : 'right'}`} style={{ fontSize: '1.25rem' }} />
                    </button>
                    <div>
                      <h2 className="AEDP-classes-title">Clases del Evento</h2>
                      <p className="AEDP-classes-subtitle">
                        Organiza las clases del evento con sus fechas y descripciones.
                      </p>
                    </div>
                  </div>
                  {hasEditEvents && (
                    <Button variant="primary" onClick={() => setShowClassModal(true)}>
                      <i className="bi-plus-circle me-2" />
                      Crear Clase
                    </Button>
                  )}
                </div>

                {isClassesExpanded && (
                <div className="AEDP-classes-content">
                  {loadingClasses ? (
                    <div className="AEDP-loading">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2">Cargando clases...</span>
                    </div>
                  ) : eventClasses.length === 0 ? (
                    <div className="AEDP-empty-state">
                      <i className="bi-calendar-x" />
                      <h5 className="AEDP-empty-title">Aún no hay clases creadas</h5>
                      <p>Organiza el evento creando clases con sus fechas y descripciones.</p>
                      {hasEditEvents && (
                        <button className="AEDP-empty-classes-button" onClick={() => setShowClassModal(true)}>
                          <i className="bi-plus-circle" />
                          Crear primera clase
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="AEDP-classes-grid">
                      {eventClasses
                        .slice()
                        .sort((a, b) => (a.orderIndex || a.orden || 0) - (b.orderIndex || b.orden || 0))
                        .map((classItem) => (
                          <div key={classItem.id} className="AEDP-class-card">
                            <div className="AEDP-class-header">
                              <div className="AEDP-class-header-left">
                                <span className="AEDP-class-order-badge">
                                  {(classItem.orderIndex !== null && classItem.orderIndex !== undefined) 
                                    ? classItem.orderIndex + 1 
                                    : (classItem.orden !== null && classItem.orden !== undefined) 
                                      ? classItem.orden + 1 
                                      : '?'}
                                </span>
                                <h5>{classItem.tituloclaseevento || classItem.nombre || 'Clase sin título'}</h5>
                              </div>
                              {hasEditEvents && (
                                <div className="AEDP-class-actions">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                      setClassDraft({
                                        id: classItem.id,
                                        tituloclaseevento: classItem.tituloclaseevento || classItem.nombre || '',
                                        descripcionbreve: classItem.descripcionbreve || classItem.descripcionBreve || '',
                                        descripcioncompleta: classItem.descripcioncompleta || classItem.descripcionCompleta || '',
                                        diaclase: classItem.diaclase || '',
                                        orderIndex: classItem.orderIndex || classItem.orden || 0, // Mantener para compatibilidad, pero no se muestra en el formulario
                                      });
                                      setShowClassModal(true);
                                    }}
                                  >
                                    <i className="bi-pencil" />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteClass(classItem.id)}
                                  >
                                    <i className="bi-trash" />
                                  </Button>
                                </div>
                              )}
                            </div>
                            <div className="AEDP-class-body">
                              {classItem.descripcionbreve || classItem.descripcionBreve ? (
                                <p className="AEDP-class-description">
                                  {classItem.descripcionbreve || classItem.descripcionBreve}
                                </p>
                              ) : null}
                              {classItem.diaclase && (
                                <div className="AEDP-class-meta">
                                  <i className="bi-calendar3" />
                                  {new Date(classItem.diaclase).toLocaleDateString('es-ES', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                )}
              </div>
            </section>

            {/* Códigos del evento */}
            <section className="AEDP-codes-section">
              <div className="AEDP-codes-container">
                <div className="AEDP-codes-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    <button
                      type="button"
                      onClick={() => setIsCodesExpanded(!isCodesExpanded)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#495057'
                      }}
                    >
                      <i className={`bi-chevron-${isCodesExpanded ? 'down' : 'right'}`} style={{ fontSize: '1.25rem' }} />
                    </button>
                    <div>
                      <h2 className="AEDP-codes-title">Códigos de Acceso</h2>
                      <p className="AEDP-codes-subtitle">
                        Gestiona los códigos de acceso que los usuarios compran para acceder al evento.
                      </p>
                    </div>
                  </div>
                  {hasEditEvents && (
                    <Button variant="primary" onClick={() => setShowGenerateCodeModal(true)}>
                      <i className="bi-plus-circle me-2" />
                      Generar Códigos
                    </Button>
                  )}
                </div>

                {isCodesExpanded && (
                <>
                {/* Estadísticas */}
                {codeStats && (
                  <div className="AEDP-codes-stats">
                    <div className="AEDP-code-stat-card">
                      <div className="AEDP-code-stat-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                        <i className="bi-hash" />
                      </div>
                      <div className="AEDP-code-stat-content">
                        <div className="AEDP-code-stat-value">{codeStats.total || 0}</div>
                        <div className="AEDP-code-stat-label">Total</div>
                      </div>
                    </div>
                    <div className="AEDP-code-stat-card">
                      <div className="AEDP-code-stat-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                        <i className="bi-check-circle" />
                      </div>
                      <div className="AEDP-code-stat-content">
                        <div className="AEDP-code-stat-value">{codeStats.available || 0}</div>
                        <div className="AEDP-code-stat-label">Disponibles</div>
                      </div>
                    </div>
                    <div className="AEDP-code-stat-card">
                      <div className="AEDP-code-stat-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                        <i className="bi-person-check" />
                      </div>
                      <div className="AEDP-code-stat-content">
                        <div className="AEDP-code-stat-value">{codeStats.assigned || 0}</div>
                        <div className="AEDP-code-stat-label">Asignados</div>
                      </div>
                    </div>
                    <div className="AEDP-code-stat-card">
                      <div className="AEDP-code-stat-icon" style={{ background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}>
                        <i className="bi-check2-circle" />
                      </div>
                      <div className="AEDP-code-stat-content">
                        <div className="AEDP-code-stat-value">{codeStats.used || 0}</div>
                        <div className="AEDP-code-stat-label">Usados</div>
                      </div>
                    </div>
                    <div className="AEDP-code-stat-card">
                      <div className="AEDP-code-stat-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                        <i className="bi-x-circle" />
                      </div>
                      <div className="AEDP-code-stat-content">
                        <div className="AEDP-code-stat-value">{codeStats.cancelled || 0}</div>
                        <div className="AEDP-code-stat-label">Cancelados</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Filtros */}
                <div className="AEDP-codes-filters">
                  <Form.Select
                    value={codeFilter}
                    onChange={(e) => setCodeFilter(e.target.value)}
                    style={{ maxWidth: '200px' }}
                  >
                    <option value="all">Todos</option>
                    <option value="available">Disponibles</option>
                    <option value="assigned">Asignados</option>
                    <option value="used">Usados</option>
                    <option value="cancelled">Cancelados</option>
                  </Form.Select>
                  <Button variant="outline-secondary" size="sm" onClick={() => Promise.all([fetchEventCodes(), fetchCodeStats()])}>
                    <i className="bi-arrow-clockwise me-2" />
                    Actualizar
                  </Button>
                  {filteredCodes.length > 0 && (
                    <span className="AEDP-codes-count">
                      Mostrando {startIndex + 1}-{Math.min(endIndex, filteredCodes.length)} de {filteredCodes.length} códigos
                    </span>
                  )}
                </div>

                {/* Tabla de códigos */}
                <div className="AEDP-codes-content">
                  {loadingCodes ? (
                    <div className="AEDP-loading">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2">Cargando códigos...</span>
                    </div>
                  ) : filteredCodes.length === 0 ? (
                    <div className="AEDP-empty-state">
                      <i className="bi-ticket-perforated" />
                      <h5 className="AEDP-empty-title">
                        {eventCodes.length === 0
                          ? 'Aún no hay códigos generados'
                          : 'No hay códigos con este filtro'}
                      </h5>
                      <p>
                        {eventCodes.length === 0
                          ? 'Genera códigos de acceso para que los usuarios puedan comprar acceso al evento.'
                          : 'Intenta cambiar el filtro para ver más códigos.'}
                      </p>
                      {hasEditEvents && eventCodes.length === 0 && (
                        <button className="AEDP-empty-classes-button" onClick={() => setShowGenerateCodeModal(true)}>
                          <i className="bi-plus-circle" />
                          Generar primeros códigos
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="AEDP-codes-table-wrapper">
                        <table className="AEDP-codes-table">
                          <thead>
                            <tr>
                              <th>Código</th>
                              <th>Estado</th>
                              <th>Usuario</th>
                              <th>Asignado</th>
                              <th>Usado</th>
                              <th>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedCodes.map((code) => (
                              <tr key={code.id}>
                                <td>
                                  <code className="AEDP-code-value">{code.code}</code>
                                </td>
                                <td>
                                  <Badge
                                    className={
                                      code.status === 'available'
                                        ? 'AEDP-badge-available'
                                        : code.status === 'assigned'
                                        ? 'AEDP-badge-assigned'
                                        : code.status === 'used'
                                        ? 'AEDP-badge-used'
                                        : 'AEDP-badge-cancelled'
                                    }
                                  >
                                    {code.status === 'available'
                                      ? 'Disponible'
                                      : code.status === 'assigned'
                                      ? 'Asignado'
                                      : code.status === 'used'
                                      ? 'Usado'
                                      : 'Cancelado'}
                                  </Badge>
                                </td>
                                <td>
                                  {code.assignedUser ? (
                                    <span>
                                      {code.assignedUser.nombre} {code.assignedUser.apellido}
                                      <br />
                                      <small className="text-muted">{code.assignedUser.email}</small>
                                    </span>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  {code.assignedAt
                                    ? new Date(code.assignedAt).toLocaleDateString('es-ES', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : '-'}
                                </td>
                                <td>
                                  {code.usedAt
                                    ? new Date(code.usedAt).toLocaleDateString('es-ES', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : '-'}
                                </td>
                                <td>
                                  {code.status !== 'cancelled' && code.status !== 'used' && hasEditEvents && (
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleCancelCode(code.id)}
                                      title="Cancelar código"
                                    >
                                      <i className="bi-x-circle" />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Paginación */}
                      {totalPages > 1 && (
                        <div className="AEDP-codes-pagination">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                          >
                            <i className="bi-chevron-left" />
                            Anterior
                          </Button>
                          <div className="AEDP-codes-pagination-pages">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                              // Mostrar solo algunas páginas alrededor de la actual
                              if (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 && page <= currentPage + 1)
                              ) {
                                return (
                                  <Button
                                    key={page}
                                    variant={currentPage === page ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setCurrentPage(page)}
                                    className="AEDP-codes-page-btn"
                                  >
                                    {page}
                                  </Button>
                                );
                              } else if (page === currentPage - 2 || page === currentPage + 2) {
                                return (
                                  <span key={page} className="AEDP-codes-pagination-ellipsis">
                                    ...
                                  </span>
                                );
                              }
                              return null;
                            })}
                          </div>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                          >
                            Siguiente
                            <i className="bi-chevron-right" />
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
                </>
                )}
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* Modal imagen */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cambiar Imagen del Evento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Seleccionar Imagen</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleImageUpload} disabled={imageLoading || !imageFile}>
            {imageLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Subiendo...
              </>
            ) : (
              'Subir Imagen'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal clase */}
      <Modal show={showClassModal} onHide={() => setShowClassModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{classDraft.id ? 'Editar Clase' : 'Crear Clase'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Título de la Clase *</Form.Label>
            <Form.Control
              value={classDraft.tituloclaseevento}
              onChange={(e) => setClassDraft({ ...classDraft, tituloclaseevento: e.target.value })}
              placeholder="Ingresa el título de la clase"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Descripción Breve</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={classDraft.descripcionbreve}
              onChange={(e) => setClassDraft({ ...classDraft, descripcionbreve: e.target.value })}
              placeholder="Descripción breve de la clase"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Descripción Completa</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={classDraft.descripcioncompleta}
              onChange={(e) => setClassDraft({ ...classDraft, descripcioncompleta: e.target.value })}
              placeholder="Descripción completa de la clase"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Asignar a Día del Evento</Form.Label>
            <Form.Select
              value={classDraft.diaclase || ''}
              onChange={(e) => setClassDraft({ ...classDraft, diaclase: e.target.value || '' })}
            >
              <option value="">Sin asignar</option>
              {eventDays.map((day) => (
                <option key={day.id} value={day.fecha}>
                  {day.titulo} - {new Date(day.fecha).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </option>
              ))}
            </Form.Select>
            {eventDays.length === 0 && (
              <Form.Text className="text-muted">
                No hay días creados. Crea días del evento primero para poder asignar clases.
              </Form.Text>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowClassModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveClass}
            disabled={classLoading || !classDraft.tituloclaseevento.trim()}
          >
            {classLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Guardando...
              </>
            ) : classDraft.id ? (
              'Actualizar'
            ) : (
              'Crear'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal día */}
      <Modal show={showDayModal} onHide={() => setShowDayModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{dayDraft.id ? 'Editar Día' : 'Crear Día'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Fecha *</Form.Label>
            <Form.Control
              type="date"
              value={dayDraft.fecha}
              onChange={(e) => setDayDraft({ ...dayDraft, fecha: e.target.value })}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Título *</Form.Label>
            <Form.Control
              value={dayDraft.titulo}
              onChange={(e) => setDayDraft({ ...dayDraft, titulo: e.target.value })}
              placeholder="Ej: Día 1, Inauguración, etc."
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Descripción</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={dayDraft.descripcion}
              onChange={(e) => setDayDraft({ ...dayDraft, descripcion: e.target.value })}
              placeholder="Descripción del día del evento"
            />
          </Form.Group>
          <div className="row">
            <Form.Group className="mb-3 col-md-6">
              <Form.Label>Hora de Inicio</Form.Label>
              <Form.Control
                type="time"
                value={dayDraft.horaInicio}
                onChange={(e) => setDayDraft({ ...dayDraft, horaInicio: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3 col-md-6">
              <Form.Label>Hora de Fin</Form.Label>
              <Form.Control
                type="time"
                value={dayDraft.horaFin}
                onChange={(e) => setDayDraft({ ...dayDraft, horaFin: e.target.value })}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDayModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveDay} disabled={dayLoading || !dayDraft.fecha || !dayDraft.titulo.trim() || (dayDraft.horaInicio && dayDraft.horaFin && dayDraft.horaInicio >= dayDraft.horaFin)}>
            {dayLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Guardando...
              </>
            ) : (
              dayDraft.id ? 'Actualizar' : 'Crear'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal generar códigos */}
      <Modal show={showGenerateCodeModal} onHide={() => setShowGenerateCodeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Generar Códigos de Acceso</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Cantidad de códigos a generar *</Form.Label>
            <Form.Control
              type="number"
              min="1"
              max="1000"
              value={codeQuantity}
              onChange={(e) => setCodeQuantity(parseInt(e.target.value) || 1)}
              placeholder="Ej: 10, 50, 100"
            />
            <Form.Text className="text-muted">
              Puedes generar entre 1 y 1000 códigos a la vez. Cada código será único de 6 caracteres alfanuméricos.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGenerateCodeModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleGenerateCodes} disabled={codeLoading || !codeQuantity || codeQuantity <= 0 || codeQuantity > 1000}>
            {codeLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Generando...
              </>
            ) : (
              'Generar Códigos'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const AdminEventosDetallePage = () => (
  <AdminNotificationsProvider>
    <AdminEventosDetalleInner />
  </AdminNotificationsProvider>
);

export default AdminEventosDetallePage;

